import type { ChatData } from './types';

export type SerializedChat = ChatData;

/**
 * Model class for chat data.
 * Encapsulates chat metadata and state.
 */
export class Chat {
  private _data: ChatData;

  constructor(data: ChatData) {
    this._data = { ...data };
  }

  /**
   * Get the chat ID.
   */
  get id(): string {
    return this._data.id;
  }

  /**
   * Get the org ID.
   */
  get orgId(): string {
    return this._data.orgId;
  }

  /**
   * Get the actor ID.
   */
  get actorId(): string {
    return this._data.actorId;
  }

  /**
   * Get the trigger source that originally created this chat.
   */
  get triggerSource(): ChatData['triggerSource'] {
    return this._data.triggerSource ?? null;
  }

  /**
   * Get the legacy agent identifier (from data graph).
   */
  get agentIdentity(): string | null {
    return this._data.agent;
  }

  /**
   * Get the agent ID (from agents table). Links to a saved agent.
   */
  get agentId(): string | null {
    return this._data.agentId ?? null;
  }

  /**
   * Get the title.
   */
  get title(): string {
    return this._data.title;
  }

  /**
   * Get the status (active or archived).
   */
  get status(): 'active' | 'archived' {
    return this._data.status;
  }

  /**
   * Get the parent chat ID (null if this is a root conversation).
   */
  get parentId(): string | null {
    return this._data.parentId ?? null;
  }

  /**
   * Get the parent's tool call ID that spawned this sub-agent (null if not a sub-agent or not set).
   */
  getParentToolCallId(): string | null {
    return this._data.parentToolCallId ?? null;
  }

  /**
   * Get the folder ID when the chat is placed in a folder (null otherwise).
   */
  get folderId(): string | null {
    return this._data.folderId ?? null;
  }

  /**
   * Get the resource ID when the chat is placed in a folder (null otherwise).
   */
  get resourceId(): string | null {
    return this._data.resourceId ?? null;
  }

  /**
   * Get the org that owns this chat for credential/cross-org purposes.
   * When in a folder, the folder's org; otherwise the org that created the chat.
   */
  get ownerOrgId(): string {
    return this._data.ownerOrgId;
  }

  /**
   * Get the metadata.
   */
  get metadata(): ChatData['metadata'] {
    return this._data.metadata;
  }

  /**
   * Get the loaded tool IDs from metadata.
   */
  getLoadedToolIds(): string[] {
    return this._data.metadata?.loadedToolIds ?? [];
  }

  /** Tool keys this chat must not invoke unless overridden by @mentions (chat metadata). */
  getExcludedToolKeys(): string[] {
    return this._data.metadata?.excludedToolKeys ?? [];
  }

  /**
   * Check if the chat is archived.
   */
  isArchived(): boolean {
    return this._data.status === 'archived';
  }

  /**
   * Get the full chat data object.
   */
  get chatData(): ChatData {
    return { ...this._data };
  }

  /**
   * Serialize this chat to a plain object.
   */
  serialize(): SerializedChat {
    return { ...this._data };
  }

  /**
   * Create a Chat from serialized data.
   */
  static fromSerialized(serialized: SerializedChat): Chat {
    return new Chat(serialized);
  }

  /**
   * Create a clone of this chat.
   */
  clone(): Chat {
    return new Chat({ ...this._data });
  }
}
